import * as THREE from 'three';
import { createTileShape, getTileTopHeight, tileWorldCenter } from './grid3d';
import type { GridGeometry } from '../../grid';
import type { MapDefinition } from '../../model/gamestate/GameState';
import type { VisualEffect } from '../tilemap/types';

export interface ThreeEffectSceneHost {
  effectGroup: THREE.Group;
}

interface BoardProjectionOptions {
  x: number;
  y: number;
  radiusTiles: number;
  color: string;
  fillOpacity: number;
  edgeOpacity: number;
  heightOffset: number;
  map: MapDefinition;
  grid: GridGeometry;
  tileSize: number;
}

function disposeObject3D(object: THREE.Object3D): void {
  object.traverse((node: THREE.Object3D) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    const material = (mesh as { material?: THREE.Material | THREE.Material[] }).material;
    if (Array.isArray(material)) {
      material.forEach((entry) => entry.dispose());
    } else {
      material?.dispose();
    }
  });
}

function createProjectedDisc(options: BoardProjectionOptions): {
  group: THREE.Group;
  fill: THREE.MeshBasicMaterial;
  edge: THREE.MeshBasicMaterial;
} {
  const group = new THREE.Group();
  const center = tileWorldCenter(options.grid, options.tileSize, options.x, options.y);
  const topY = getTileTopHeight(options.map.tiles[Math.round(options.y)]?.[Math.round(options.x)] ?? options.map.tiles[0][0]);
  group.position.set(center.x, topY + options.heightOffset, center.z);

  const radius = Math.max(0.55, options.radiusTiles + 0.25);
  const shape = new THREE.Shape();
  shape.absarc(0, 0, radius, 0, Math.PI * 2);

  const fillMaterial = new THREE.MeshBasicMaterial({
    color: options.color,
    transparent: true,
    opacity: options.fillOpacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const fillMesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), fillMaterial);
  fillMesh.rotation.x = -Math.PI / 2;
  group.add(fillMesh);

  const edgeMaterial = new THREE.MeshBasicMaterial({
    color: options.color,
    transparent: true,
    opacity: options.edgeOpacity,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ringShape = new THREE.Shape();
  ringShape.absarc(0, 0, radius, 0, Math.PI * 2);
  const hole = new THREE.Path();
  hole.absarc(0, 0, Math.max(0.01, radius - 0.08), 0, Math.PI * 2);
  ringShape.holes.push(hole);
  const edgeMesh = new THREE.Mesh(new THREE.ShapeGeometry(ringShape), edgeMaterial);
  edgeMesh.rotation.x = -Math.PI / 2;
  edgeMesh.position.y = 0.001;
  group.add(edgeMesh);

  return {
    group,
    fill: fillMaterial,
    edge: edgeMaterial,
  };
}

export class ThreeMeteorTargetEffect implements VisualEffect {
  private elapsed = 0;
  private readonly durationSeconds: number;
  private readonly pulseSpeed = Math.PI * 4;
  private readonly disc: ReturnType<typeof createProjectedDisc>;
  private host: ThreeEffectSceneHost | null = null;

  public constructor(options: {
    x: number;
    y: number;
    radiusTiles: number;
    durationSeconds: number;
    color: string;
    map: MapDefinition;
    grid: GridGeometry;
    tileSize: number;
  }) {
    this.durationSeconds = Math.max(0.35, options.durationSeconds);
    this.disc = createProjectedDisc({
      x: options.x,
      y: options.y,
      radiusTiles: options.radiusTiles,
      color: options.color,
      fillOpacity: 0.2,
      edgeOpacity: 0.92,
      heightOffset: 0.018,
      map: options.map,
      grid: options.grid,
      tileSize: options.tileSize,
    });
  }

  public update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
  }

  public attachToScene(scene: unknown): void {
    const host = scene as ThreeEffectSceneHost;
    this.host = host;
    host.effectGroup.add(this.disc.group);
  }

  public updateScene(): void {
    const progress = Math.min(this.elapsed / this.durationSeconds, 1);
    const pulse = 1 + 0.08 * Math.sin(this.elapsed * this.pulseSpeed);
    this.disc.group.scale.setScalar(pulse);
    this.disc.fill.opacity = 0.22 * (1 - progress * 0.35);
    this.disc.edge.opacity = 0.9 - progress * 0.18;
  }

  public disposeScene(): void {
    if (this.host) {
      this.host.effectGroup.remove(this.disc.group);
      this.host = null;
    }
    disposeObject3D(this.disc.group);
  }

  public isComplete(): boolean {
    return this.elapsed >= this.durationSeconds;
  }
}

export class ThreeMeteorImpactEffect implements VisualEffect {
  private elapsed = 0;
  private readonly durationSeconds = 0.5;
  private readonly disc: ReturnType<typeof createProjectedDisc>;
  private host: ThreeEffectSceneHost | null = null;

  public constructor(options: {
    x: number;
    y: number;
    radius: number;
    map: MapDefinition;
    grid: GridGeometry;
    tileSize: number;
  }) {
    this.disc = createProjectedDisc({
      x: options.x,
      y: options.y,
      radiusTiles: options.radius,
      color: '#ff7a18',
      fillOpacity: 0.5,
      edgeOpacity: 0.95,
      heightOffset: 0.024,
      map: options.map,
      grid: options.grid,
      tileSize: options.tileSize,
    });
    this.disc.group.scale.setScalar(0.2);
  }

  public update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
  }

  public attachToScene(scene: unknown): void {
    const host = scene as ThreeEffectSceneHost;
    this.host = host;
    host.effectGroup.add(this.disc.group);
  }

  public updateScene(): void {
    const progress = Math.min(this.elapsed / this.durationSeconds, 1);
    const radiusScale = Math.sqrt(Math.max(0.05, progress));
    this.disc.group.scale.setScalar(radiusScale);
    this.disc.fill.opacity = 0.55 * (1 - progress);
    this.disc.edge.opacity = 0.95 * (1 - progress * 0.6);
  }

  public disposeScene(): void {
    if (this.host) {
      this.host.effectGroup.remove(this.disc.group);
      this.host = null;
    }
    disposeObject3D(this.disc.group);
  }

  public isComplete(): boolean {
    return this.elapsed >= this.durationSeconds;
  }
}

export class ThreeSmiteStrikeEffect implements VisualEffect {
  private elapsed = 0;
  private readonly durationSeconds = 0.5;
  private readonly group = new THREE.Group();
  private readonly boltMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly boltBaseOpacities: number[] = [];
  private readonly flashMaterial: THREE.MeshBasicMaterial;
  private host: ThreeEffectSceneHost | null = null;

  public constructor(options: {
    x: number;
    y: number;
    map: MapDefinition;
    grid: GridGeometry;
    tileSize: number;
  }) {
    const center = tileWorldCenter(options.grid, options.tileSize, options.x, options.y);
    const topY = getTileTopHeight(options.map.tiles[Math.round(options.y)]?.[Math.round(options.x)] ?? options.map.tiles[0][0]);
    this.group.position.set(center.x, topY, center.z);

    const boltLayers = [
      { radius: 0.16, color: '#fbbf24', opacity: 0.18 },
      { radius: 0.09, color: '#fde68a', opacity: 0.45 },
      { radius: 0.04, color: '#fff7cc', opacity: 0.95 },
    ];
    for (const layer of boltLayers) {
      const material = new THREE.MeshBasicMaterial({
        color: layer.color,
        transparent: true,
        opacity: layer.opacity,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(layer.radius, layer.radius * 0.6, 3.2, 10), material);
      mesh.position.y = 1.55;
      this.group.add(mesh);
      this.boltMaterials.push(material);
      this.boltBaseOpacities.push(layer.opacity);
    }

    this.flashMaterial = new THREE.MeshBasicMaterial({
      color: '#fff0a8',
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const flash = new THREE.Mesh(new THREE.CircleGeometry(0.75, 32), this.flashMaterial);
    flash.rotation.x = -Math.PI / 2;
    flash.position.y = 0.02;
    this.group.add(flash);
  }

  public update(deltaSeconds: number): void {
    this.elapsed += deltaSeconds;
  }

  public attachToScene(scene: unknown): void {
    const host = scene as ThreeEffectSceneHost;
    this.host = host;
    host.effectGroup.add(this.group);
  }

  public updateScene(): void {
    const progress = Math.min(this.elapsed / this.durationSeconds, 1);
    const strikeProgress = Math.min(progress / 0.4, 1);
    const fadeProgress = progress > 0.4 ? (progress - 0.4) / 0.6 : 0;
    const boltAlpha = (1 - fadeProgress) * Math.min(strikeProgress * 4, 1);

    for (const material of this.boltMaterials) {
      const index = this.boltMaterials.indexOf(material);
      material.opacity = this.boltBaseOpacities[index] * boltAlpha;
    }
    const flashAlpha = Math.max(0, 1 - progress / 0.25);
    this.flashMaterial.opacity = flashAlpha * 0.9;
    const flashScale = 1 + progress * 0.5;
    this.group.children[this.group.children.length - 1]?.scale.set(flashScale, flashScale, flashScale);
  }

  public disposeScene(): void {
    if (this.host) {
      this.host.effectGroup.remove(this.group);
      this.host = null;
    }
    disposeObject3D(this.group);
  }

  public isComplete(): boolean {
    return this.elapsed >= this.durationSeconds;
  }
}
